import { IAccount } from 'dto/access-management/account-dto';
import { ComponentDto } from 'dto/raptor-component.dto';
import { ComponentVersionDto } from 'dto/raptor-component-version.dto';
import { useLoadingStates } from 'hooks/use-loading.hook';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountStore, useAuthStore, useRaptorComponentsStore } from 'store';
import { notification } from 'ui-lib';

const MISSING_INFORMATION_MESSAGE = 'Missing account or component information';
const LOAD_ERROR_MESSAGE = 'Failed to load component version form';

interface IUseComponentVersionFormRequest {
    accountUuid?: string | null;
    componentUuid?: string;
    componentVersionUuid?: string;
}

export const useComponentVersionForm = (request: IUseComponentVersionFormRequest) => {
    const accountStore = useAccountStore();
    const { currentUser } = useAuthStore();
    const componentsStore = useRaptorComponentsStore();
    const loadingHook = useLoadingStates();

    const [account, setAccount] = React.useState<IAccount>();
    const [component, setComponent] = React.useState<ComponentDto>();
    const [componentVersion, setComponentVersion] = React.useState<ComponentVersionDto>();
    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        let isMounted = true;

        const loadCommonFormData = async () => {
            setLoaded(false);
            setAccount(undefined);
            setComponent(undefined);
            setComponentVersion(undefined);

            const {
                accountUuid: requestAccountUuid,
                componentUuid,
                componentVersionUuid,
            } = request;

            const accountUuid = requestAccountUuid || currentUser.accountUuid;
            if (!accountUuid || !componentUuid) {
                notification.error({
                    message: MISSING_INFORMATION_MESSAGE,
                });
                setLoaded(true);
                return;
            }

            const result = await loadingHook.withLoading(async () => {
                const accounts = await accountStore.getAccounts();
                const selectedAccount = accounts.find((accountItem) => accountItem.uuid === accountUuid);

                if (!selectedAccount) {
                    throw new Error('Selected account was not found');
                }

                const [
                    loadedComponent,
                    loadedComponentVersion,
                ] = await Promise.all([
                    componentsStore.getComponent(selectedAccount.id, componentUuid),
                    componentVersionUuid
                        ? componentsStore.getComponentVersion(selectedAccount.id, componentVersionUuid)
                        : Promise.resolve(undefined),
                ]);

                return {
                    selectedAccount,
                    loadedComponent,
                    loadedComponentVersion,
                };
            });

            if (!isMounted) {
                return;
            }

            setLoaded(true);

            if (result.isFail()) {
                notification.error({
                    message: LOAD_ERROR_MESSAGE,
                    description: result.errorMessage,
                });
                return;
            }

            const {
                selectedAccount,
                loadedComponent,
                loadedComponentVersion,
            } = result.getOrThrow();

            setAccount(selectedAccount);
            setComponent(loadedComponent);
            setComponentVersion(loadedComponentVersion);
        };

        loadCommonFormData();

        return () => {
            isMounted = false;
        };
    }, [request.accountUuid, request.componentUuid, request.componentVersionUuid]);

    return {
        account,
        component,
        componentVersion,
        loaded,
        loadingHook,
    };
};
